<template>
  <div class="dashboard-body-futuristic">
    <main class="futuristic-container">
      <!-- Sidebar -->
      <AppSidebar activeItem="bookmarks" />

      <!-- Main Content Area -->
      <section class="dashboard-card-futuristic dashboard-card-scrollable">
  <div class="space-y-6 animate-slide-up text-left">
    <!-- Header -->
    <div class="flex items-center justify-between flex-wrap gap-4">
      <div>
        <h1 class="flex items-center gap-2">
          <StarSolidIcon class="w-6 h-6 text-amber-500" aria-hidden="true" />
          Bookmarks
        </h1>
        <p class="text-slate-500 mt-1">
          <span v-if="bookmarks.length">
            <span class="font-semibold text-slate-900">{{ bookmarks.length }}</span>
            saved tender{{ bookmarks.length === 1 ? '' : 's' }}
          </span>
          <span v-else>No saved tenders</span>
        </p>
      </div>

      <div class="flex items-center gap-3">
        <!-- View toggle -->
        <div v-if="bookmarks.length" class="flex items-center bg-white border border-slate-200 rounded-lg p-1 shadow-soft">
          <button
            type="button"
            @click="viewMode = 'table'"
            :aria-pressed="viewMode === 'table'"
            class="px-3 py-1.5 rounded-md flex items-center gap-1.5 text-sm font-medium transition-colors duration-200"
            :class="viewMode === 'table' ? 'bg-[#0071C5] text-white shadow-soft' : 'text-slate-600 hover:text-slate-800'"
          >
            <TableCellsIcon class="w-4 h-4" aria-hidden="true" />
            Table
          </button>
          <button
            type="button"
            @click="viewMode = 'cards'"
            :aria-pressed="viewMode === 'cards'"
            class="px-3 py-1.5 rounded-md flex items-center gap-1.5 text-sm font-medium transition-colors duration-200"
            :class="viewMode === 'cards' ? 'bg-[#0071C5] text-white shadow-soft' : 'text-slate-600 hover:text-slate-800'"
          >
            <Squares2X2Icon class="w-4 h-4" aria-hidden="true" />
            Cards
          </button>
        </div>

        <button
          v-if="bookmarks.length"
          type="button"
          @click="confirmClear"
          class="btn-ghost flex items-center gap-2 text-red-700 hover:bg-red-50 hover:border-red-200"
        >
          <TrashIcon class="w-4 h-4" aria-hidden="true" />
          Clear All
        </button>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="!bookmarks.length" class="card p-16 text-center">
      <div class="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center">
        <StarIcon class="w-12 h-12 text-amber-500" aria-hidden="true" />
      </div>
      <h2 class="text-xl font-bold text-slate-900 mb-2">No Bookmarks Yet</h2>
      <p class="text-slate-600 mb-6 max-w-md mx-auto">
        Select the star on any tender in Tender Search to save it here.
        Then choose Go to upload its PDFs for analysis.
      </p>
      <router-link
        :to="{ name: 'Tenders' }"
        class="inline-flex items-center gap-2 bg-[#0071C5] hover:bg-[#005a9e] text-white text-sm font-semibold px-5 py-2.5 rounded-[10px] transition-colors"
      >
        <DocumentTextIcon class="w-4 h-4" aria-hidden="true" />
        Go to Tender Search
      </router-link>
    </div>

    <!-- Sort bar -->
    <div v-else class="flex items-center gap-3 text-sm">
      <span class="text-slate-600">Sort by:</span>
      <button
        v-for="opt in sortOptions"
        :key="opt.value"
        type="button"
        @click="sortBy = opt.value"
        :aria-pressed="sortBy === opt.value"
        class="px-3 py-1 rounded-full font-medium transition-colors duration-200"
        :class="sortBy === opt.value
          ? 'bg-[#0071C5] text-white shadow-soft'
          : 'bg-white border border-slate-200 text-slate-600 hover:border-primary-300'"
      >
        {{ opt.label }}
      </button>
    </div>

    <!-- Table view -->
    <TenderTable
      v-if="bookmarks.length && viewMode === 'table'"
      :tenders="sortedBookmarks"
      :showProjectStatus="true"
    />

    <!-- Card view -->
    <div v-else-if="bookmarks.length && viewMode === 'cards'" class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <TenderCard
        v-for="tender in sortedBookmarks"
        :key="tender.id"
        :tender="tender"
        :showProjectStatus="true"
      />
    </div>

  </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useToast } from "vue-toastification";
import {
  StarIcon,
  TrashIcon,
  DocumentTextIcon,
  TableCellsIcon,
  Squares2X2Icon,
} from "@heroicons/vue/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/vue/24/solid";

import AppSidebar from "./Sidebar.vue";
import TenderCard from "./TenderCard.vue";
import TenderTable from "./TenderTable.vue";
import { useBookmarks } from "../store/bookmarks";
import { confirmAction } from "../utils/confirm";

const { bookmarks, load, clearAll: clearAllBookmarks } = useBookmarks();
const toast = useToast();

onMounted(load);

const sortBy = ref("newest");
const viewMode = ref(localStorage.getItem("bookmark_view_mode") || "table");
watch(viewMode, (val) => localStorage.setItem("bookmark_view_mode", val));

const sortOptions = [
  { value: "newest",  label: "Recently Bookmarked" },
  { value: "closing", label: "Closing Soonest" },
  { value: "title",   label: "Title A-Z" },
];

const sortedBookmarks = computed(() => {
  // normalize: TenderCard expects tender.id but API returns tender_id
  const list = bookmarks.value.map((b) => ({ ...b, id: b.tender_id ?? b.id }));
  if (sortBy.value === "newest") {
    return list.sort((a, b) =>
      new Date(b.bookmarked_at || 0) - new Date(a.bookmarked_at || 0)
    );
  }
  if (sortBy.value === "closing") {
    return list.sort((a, b) =>
      new Date(a.closing_date || 0) - new Date(b.closing_date || 0)
    );
  }
  if (sortBy.value === "title") {
    return list.sort((a, b) =>
      (a.title || "").localeCompare(b.title || "")
    );
  }
  return list;
});

async function confirmClear() {
  const count = bookmarks.value.length;
  const ok = await confirmAction({
    title: "Clear All Bookmarks?",
    message: `This removes all ${count} saved tender${count === 1 ? "" : "s"}, including any you're preparing a bid for. You can't undo this.`,
    confirmLabel: "Clear All",
    destructive: true,
  });
  if (!ok) return;
  try {
    await clearAllBookmarks();
    toast.success("All bookmarks cleared");
  } catch {
    toast.error("Couldn't clear bookmarks. Check your connection and try again.");
  }
}
</script>
